import { useEffect, useMemo, useRef, useState } from 'react'
import * as pdfjs from 'pdfjs-dist'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'
import { EventBus, PDFLinkService } from 'pdfjs-dist/web/pdf_viewer.mjs'
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import 'pdfjs-dist/web/pdf_viewer.css'
import ErrorAlert from '@components/Alerts/ErrorAlert'

// Render PDFs off the main thread.
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

// pdf.js LinkTarget.BLANK — open external links in a new tab.
const EXTERNAL_LINK_TARGET_BLANK = 2

interface PdfDocumentProps {
  /** URL of the PDF to render (e.g. an imported asset). */
  file: string
  /** Optional URL for an "open the file directly" fallback if rendering fails. */
  downloadUrl?: string
}

interface PdfPageProps {
  pdf: PDFDocumentProxy
  pageNumber: number
  /** CSS width, in px, the rendered page should occupy. */
  width: number
  linkService: PDFLinkService
}

/**
 * Renders one PDF page as a "paper" sheet: a crisp canvas with an invisible text layer
 * (selectable / searchable text) and an annotation layer (clickable links) overlaid on top.
 */
const PdfPage = ({ pdf, pageNumber, width, linkService }: PdfPageProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const textLayerRef = useRef<HTMLDivElement>(null)
  const annotationLayerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!width) return

    let cancelled = false
    let renderTask: ReturnType<PDFPageProxy['render']> | null = null

    const run = async () => {
      const page = await pdf.getPage(pageNumber)
      if (cancelled) return

      const unscaled = page.getViewport({ scale: 1 })
      const scale = width / unscaled.width
      const viewport = page.getViewport({ scale })

      const container = containerRef.current
      const canvas = canvasRef.current
      const textDiv = textLayerRef.current
      const annotationDiv = annotationLayerRef.current
      const context = canvas?.getContext('2d')
      if (!container || !canvas || !textDiv || !annotationDiv || !context) return

      // Render the canvas at a higher pixel density than it is displayed (supersampling) so
      // small text stays sharp, accounting for high-DPI screens. The CSS size still matches
      // the page exactly, so the text/annotation layers line up. Capped to bound memory.
      const outputScale = Math.min(Math.max(window.devicePixelRatio || 1, 2), 3)
      canvas.width = Math.floor(viewport.width * outputScale)
      canvas.height = Math.floor(viewport.height * outputScale)
      canvas.style.width = `${Math.floor(viewport.width)}px`
      canvas.style.height = `${Math.floor(viewport.height)}px`
      container.style.width = `${Math.floor(viewport.width)}px`
      container.style.height = `${Math.floor(viewport.height)}px`
      // The text/annotation layers position their children relative to this scale factor.
      container.style.setProperty('--scale-factor', `${scale}`)

      renderTask = page.render({
        canvasContext: context,
        viewport,
        transform: [outputScale, 0, 0, outputScale, 0, 0]
      })
      await renderTask.promise
      if (cancelled) return

      // Selectable text layer.
      textDiv.replaceChildren()
      const textContent = await page.getTextContent()
      if (cancelled) return
      await new pdfjs.TextLayer({ textContentSource: textContent, container: textDiv, viewport }).render()

      // Clickable links (annotation layer).
      annotationDiv.replaceChildren()
      const annotations = await page.getAnnotations()
      if (cancelled) return
      const annotationLayer = new pdfjs.AnnotationLayer({
        div: annotationDiv,
        page,
        viewport,
        accessibilityManager: undefined,
        annotationCanvasMap: undefined,
        annotationEditorUIManager: undefined,
        structTreeLayer: undefined
      })
      await annotationLayer.render({
        annotations,
        page,
        viewport,
        linkService,
        div: annotationDiv,
        renderForms: false
      })
    }

    run().catch(() => {
      /* Failures bubble up via the document-level load handler in PdfDocument. */
    })

    return () => {
      cancelled = true
      renderTask?.cancel?.()
    }
  }, [pdf, pageNumber, width, linkService])

  return (
    <div
      ref={containerRef}
      className='relative mx-auto overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-base-300/60'
    >
      <canvas
        ref={canvasRef}
        className='block'
      />
      <div
        ref={textLayerRef}
        className='textLayer'
      />
      <div
        ref={annotationLayerRef}
        className='annotationLayer'
      />
    </div>
  )
}

/**
 * Renders every page of a PDF as a continuous stack of "paper" sheets so it reads like a
 * document rather than a PDF viewer — no toolbar, page counter, or navigation chrome — while
 * keeping the text selectable and links clickable. The pages size themselves to fill the
 * available width and re-render responsively.
 */
const PdfDocument = ({ file, downloadUrl }: PdfDocumentProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [width, setWidth] = useState(0)
  const [hasError, setHasError] = useState(false)

  const linkService = useMemo(
    () => new PDFLinkService({ eventBus: new EventBus(), externalLinkTarget: EXTERNAL_LINK_TARGET_BLANK }),
    []
  )

  // Load the document once.
  useEffect(() => {
    let cancelled = false
    const loadingTask = pdfjs.getDocument(file)

    loadingTask.promise
      .then((loaded) => {
        if (cancelled) {
          loaded.destroy()
          return
        }
        linkService.setDocument(loaded, null)
        setPdf(loaded)
      })
      .catch(() => {
        if (!cancelled) setHasError(true)
      })

    return () => {
      cancelled = true
      loadingTask.destroy()
    }
  }, [file, linkService])

  // Track the available width so pages render at the column width.
  useEffect(() => {
    const element = wrapperRef.current
    if (!element) return

    const update = () => setWidth(Math.floor(element.getBoundingClientRect().width))
    update()

    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={wrapperRef}>
      {hasError ? (
        <div className='w-full'>
          <ErrorAlert
            title='Unable to display the document'
            error='The document could not be rendered in your browser.'
          />
          {downloadUrl && (
            <a
              href={downloadUrl}
              target='_blank'
              rel='noreferrer'
              className='link link-primary mt-3 inline-block text-sm'
            >
              Open the PDF directly
            </a>
          )}
        </div>
      ) : !pdf || !width ? (
        <div className='py-10 text-center text-sm text-base-content/60'>Loading document…</div>
      ) : (
        <div className='flex flex-col items-center gap-6'>
          {Array.from({ length: pdf.numPages }, (_, index) => (
            <PdfPage
              key={index + 1}
              pdf={pdf}
              pageNumber={index + 1}
              width={width}
              linkService={linkService}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default PdfDocument
